import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile, useUpdateUserProfile } from '../hooks/useSettingsQueries';
import { applyTheme } from './applyTheme';
import { ARCHON_THEMES, DEFAULT_THEME, normalizeTheme } from './archonTheme';
import {
  DEFAULT_BACKGROUND_EFFECT,
  normalizeBackgroundEffect,
} from './backgroundEffects';
import {
  getStoredBackgroundEffect,
  setStoredBackgroundEffect,
} from './backgroundEffectStorage';
import { getStoredTheme, setStoredTheme } from './themeStorage';

const ThemeContext = createContext(null);

function resolveBootTheme() {
  return applyTheme(getStoredTheme());
}

/** @returns {{
 *   theme: import('./archonTheme').ArchonThemeId,
 *   setTheme: (t: import('./archonTheme').ArchonThemeId) => void,
 *   backgroundEffect: import('./backgroundEffects').ArchonBackgroundEffectId,
 *   setBackgroundEffect: (e: import('./backgroundEffects').ArchonBackgroundEffectId) => void,
 *   isDark: boolean,
 *   isReady: boolean,
 * }} */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const { data: profile, isSuccess: profileLoaded } = useCurrentUserProfile({
    enabled: Boolean(user),
  });
  const updateProfile = useUpdateUserProfile();

  const [theme, setThemeState] = useState(resolveBootTheme);
  const [backgroundEffect, setBackgroundEffectState] = useState(() => getStoredBackgroundEffect());
  const [isReady, setIsReady] = useState(!user);
  const hydratedFromBackend = useRef(false);
  const syncThemeTimerRef = useRef(null);
  const syncEffectTimerRef = useRef(null);

  const applyAndStoreTheme = useCallback((nextTheme) => {
    const resolved = applyTheme(nextTheme);
    setThemeState(resolved);
    setStoredTheme(resolved);
    return resolved;
  }, []);

  const applyAndStoreEffect = useCallback((nextEffect) => {
    const resolved = normalizeBackgroundEffect(nextEffect);
    setBackgroundEffectState(resolved);
    setStoredBackgroundEffect(resolved);
    return resolved;
  }, []);

  const syncThemeToBackend = useCallback(
    (nextTheme) => {
      if (!user) return;
      if (syncThemeTimerRef.current) {
        clearTimeout(syncThemeTimerRef.current);
      }
      syncThemeTimerRef.current = setTimeout(() => {
        updateProfile.mutate({ preferred_theme: nextTheme });
      }, 400);
    },
    [updateProfile, user],
  );

  const syncEffectToBackend = useCallback(
    (nextEffect) => {
      if (!user) return;
      if (syncEffectTimerRef.current) {
        clearTimeout(syncEffectTimerRef.current);
      }
      syncEffectTimerRef.current = setTimeout(() => {
        updateProfile.mutate({ preferred_background_effect: nextEffect });
      }, 400);
    },
    [updateProfile, user],
  );

  const setTheme = useCallback(
    (nextTheme) => {
      const resolved = applyAndStoreTheme(nextTheme);
      syncThemeToBackend(resolved);
    },
    [applyAndStoreTheme, syncThemeToBackend],
  );

  const setBackgroundEffect = useCallback(
    (nextEffect) => {
      const resolved = applyAndStoreEffect(nextEffect);
      syncEffectToBackend(resolved);
    },
    [applyAndStoreEffect, syncEffectToBackend],
  );

  useEffect(() => {
    if (!user) {
      hydratedFromBackend.current = false;
      setIsReady(true);
      return;
    }
    if (!profileLoaded) return;

    const backendTheme = normalizeTheme(profile?.preferred_theme);
    const localTheme = getStoredTheme();
    const backendEffect = normalizeBackgroundEffect(
      profile?.preferred_background_effect ?? DEFAULT_BACKGROUND_EFFECT,
    );
    const localEffect = getStoredBackgroundEffect();

    if (!hydratedFromBackend.current) {
      hydratedFromBackend.current = true;
      if (backendTheme !== localTheme) {
        applyAndStoreTheme(backendTheme);
      }
      if (backendEffect !== localEffect) {
        applyAndStoreEffect(backendEffect);
      }
      setIsReady(true);
      return;
    }

    if (profile?.preferred_theme && backendTheme !== theme) {
      applyAndStoreTheme(backendTheme);
    }
    if (
      profile?.preferred_background_effect &&
      backendEffect !== backgroundEffect
    ) {
      applyAndStoreEffect(backendEffect);
    }
  }, [
    user,
    profileLoaded,
    profile?.preferred_theme,
    profile?.preferred_background_effect,
    applyAndStoreTheme,
    applyAndStoreEffect,
    theme,
    backgroundEffect,
  ]);

  useEffect(() => {
    return () => {
      if (syncThemeTimerRef.current) {
        clearTimeout(syncThemeTimerRef.current);
      }
      if (syncEffectTimerRef.current) {
        clearTimeout(syncEffectTimerRef.current);
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      backgroundEffect,
      setBackgroundEffect,
      isDark: theme === ARCHON_THEMES.DARK,
      isReady,
    }),
    [theme, setTheme, backgroundEffect, setBackgroundEffect, isReady],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export { ARCHON_THEMES, DEFAULT_THEME };
